<template>
  <div class="admin-panel">
    <div v-if="!isAuthenticated" class="login-container">
      <div class="login-card">
        <h2>Acesso Restrito 🛡️</h2>
        <form @submit.prevent="login">
          <div class="form-group">
            <label>Utilizador</label>
            <input v-model="credentials.username" type="text" placeholder="Usuário" required autocomplete="username"/>
          </div>
          <div class="form-group">
            <label>Password</label>
            <input v-model="credentials.password" type="password" placeholder="••••••" required autocomplete="current-password"/>
          </div>
          <button type="submit" class="login-btn" :disabled="loading">
            {{ loading ? 'Entrando...' : 'Entrar' }}
          </button>
        </form>
      </div>
    </div>
    
    <div v-else class="dashboard-wrapper">
      <header class="admin-header-bar">
        <div class="header-content">
          <button class="refresh-btn" @click="fetchData" :disabled="loading">
            <span class="material-icons-outlined icon">refresh</span>
            <span class="btn-text">{{ 'Atualizar' }}</span>
          </button>
          
          <h2>Painel Tatu 🛡️</h2>
          
          <button class="logout-btn" @click="logout">
            <span class="material-icons-outlined icon">logout</span>
            <span class="btn-text">Sair</span>
          </button>
        </div>
        
        <div class="tabs">
          <button :class="{ active: activeTab === 'dashboard' }" @click="activeTab = 'dashboard'">
            📊 Visão Geral
          </button>
          <button :class="{ active: activeTab === 'pedidos' }" @click="activeTab = 'pedidos'">
            📦 Pedidos
          </button>
          <button :class="{ active: activeTab === 'inventario' }" @click="activeTab = 'inventario'">
            🐔 Estoque
          </button>
          <button :class="{ active: activeTab === 'avaliacoes' }" @click="activeTab = 'avaliacoes'">
            ⭐ Avaliações
          </button>
          <button :class="{ active: activeTab === 'webhooks' }" @click="activeTab = 'webhooks'">
            🔔 Logs
          </button>
        </div>
      </header>
      
      <main class="admin-content">
        <AdminDashboard
          v-if="activeTab === 'dashboard'"
          :dashboardData="dashboardData"
        />
        
        <AdminPedidos
          v-if="activeTab === 'pedidos'"
          :pedidos="pedidos"
          @refresh="fetchData"
        />
        
        <AdminEstoque
          v-if="activeTab === 'inventario'"
          :inventario="inventario"
          :produtos="produtos"
          @refresh="fetchData"
        />
        
        <AdminAvaliacoes v-if="activeTab === 'avaliacoes'" />
        
        <AdminWebhooks
          v-if="activeTab === 'webhooks'"
          :logs="logs"
        />
      </main>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import { AlertBus } from '@/plugins/AlertBus.js';
import AdminPedidos from './admin/pedidos/AdminPedidos.vue';
import AdminEstoque from './AdminEstoque.vue';
import AdminWebhooks from './AdminWebhooks.vue';
import AdminDashboard from "@/components/AdminDashboard.vue";
import AdminAvaliacoes from "@/components/AdminAvaliacoes.vue";

export default {
  name: 'Admin',
  components: {
    AdminAvaliacoes,
    AdminDashboard,
    AdminPedidos,
    AdminEstoque,
    AdminWebhooks
  },
  data() {
    return {
      isAuthenticated: false,
      loading: false,
      credentials: { username: '', password: '' },
      activeTab: 'dashboard',
      pedidos: [],
      inventario: [],
      produtos: [],
      logs: [],
      dashboardData: {
        kpis: { faturamento: 0, vendas: 0, ticketMedio: 0, pendentes: 0 },
        topProdutos: [],
        graficoVendas: { labels: [], data: [] }
      }
    };
  },
  watch: {
    activeTab(newTab) {
      // Atualizar a URL via router (sem recarregar)
      if (this.$route.query.tab !== newTab) {
        this.$router.replace({ query: { ...this.$route.query, tab: newTab } }).catch(()=>{});
      }
    }
  },
  mounted() {
    const token = localStorage.getItem('tatu_token');
    if (token) {
      this.isAuthenticated = true;
      
      const tabFromUrl = this.$route.query.tab;
      const validTabs = ['dashboard', 'pedidos', 'inventario', 'webhooks', 'avaliacoes'];
      
      if (tabFromUrl && validTabs.includes(tabFromUrl)) {
        this.activeTab = tabFromUrl;
      }
      this.fetchData();
    }
  },
  methods: {
    async login() {
      this.loading = true;
      try {
        const res = await api.post(`/api/auth/login`, this.credentials);
        localStorage.setItem('tatu_token', res.data.token);
        this.isAuthenticated = true;
        this.fetchData();
      } catch (err) {
        let msg = 'Erro de conexão com o servidor.';
        
        if (err.response) {
          if (err.response.data && err.response.data.error) {
            msg = err.response.data.error;
          }
          else if (err.response.status === 401) {
            msg = 'Credenciais incorretas.';
          }
          else if (err.response.status === 429) {
            msg = 'Muitas tentativas. Tente mais tarde.';
          }
        }
        AlertBus.$emit('show-alert', { message: msg, type: 'erro', position: 'top-center' });
      } finally {
        this.loading = false;
      }
    },
    logout() {
      localStorage.removeItem('tatu_token');
      this.isAuthenticated = false;
      this.pedidos = [];
      this.inventario = [];
      this.logs = [];
      this.credentials = { username: '', password: '' };
      history.replaceState(null, null, '#/admin');
    },
    async fetchData() {
      this.loading = true;
      
      try {
        const [resPedidos, resLogs, resInventario, resDashboard, resProdutos] = await Promise.all([
          api.get(`/api/admin/pedidos`),
          api.get(`/api/admin/webhooks`),
          api.get(`/api/admin/inventario`),
          api.get(`/api/admin/dashboard`),
          api.get(`/api/admin/produtos`)
        ]);
        
        this.pedidos = resPedidos.data;
        this.logs = resLogs.data;
        this.inventario = resInventario.data;
        this.dashboardData = resDashboard.data;
        this.produtos = resProdutos.data;
      } catch (err) {
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          this.logout();
          AlertBus.$emit('show-alert', { message: 'Sessão expirada.', type: 'alerta' });
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped src="@/assets/admin-styles.css"></style>
<style scoped>
/* Reset básico */
* { box-sizing: border-box; }

/* Estilos Específicos do Layout Admin (que não são de tabela) */
.admin-panel {
  background-color: var(--bg-main); color: var(--text-main);
  min-height: 100vh;
  min-height: 100dvh; /* Altura total mobile */
  width: 100%;
  display: flex; flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  overflow-x: hidden;
}
.dashboard-wrapper { display: flex; flex-direction: column; flex: 1; }

.admin-header-bar {
  background-color: var(--bg-header); padding: 20px;
  border-bottom: 1px solid var(--border-light); box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* --- LAYOUT DO HEADER --- */
.header-content {
  display: flex;
  align-items: center;
  /* Desktop: Título na esquerda, botões na direita */
  justify-content: flex-end;
  max-width: 95%; width: 95%; margin: 0 auto 20px;
  gap: 10px;
}

/* Ordem Desktop (Flex Order) */
.header-content h2 {
  margin: 0; color: var(--color-accent); font-size: 1.8rem;
  margin-right: auto; /* Empurra tudo para a direita */
  order: 1;
}

.refresh-btn {
  order: 2;
}

.logout-btn {
  order: 3;
}

/* Estilos Base dos Botões */
.refresh-btn, .logout-btn {
  display: flex; align-items: center; gap: 5px;
  border-radius: 6px; cursor: pointer; font-size: 0.9rem;
  padding: 8px 16px;
}

.logout-btn {
  background-color: transparent; border: 1px solid #ff5252; color: #ff5252;
}
.logout-btn:hover { background-color: rgba(255, 82, 82, 0.1); }

.refresh-btn {
  background-color: var(--bg-card); border: 1px solid var(--border-light); color: var(--text-main);
}
.refresh-btn:hover:not(:disabled) { background-color: var(--bg-icon-button-hover); }


/* --- LOGIN --- */
.login-container { display: flex; align-items: center; justify-content: center; flex: 1; padding: 20px; }
.login-card {
  background-color: var(--bg-card); padding: 40px; border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.3); width: 100%; max-width: 400px;
  border: 1px solid var(--border-light); text-align: center;
}
.form-group { margin-bottom: 20px; text-align: left; }
.form-group label { display: block; margin-bottom: 8px; font-weight: bold; color: var(--text-secondary); }
.form-group input {
  width: 100%; padding: 12px; border-radius: 4px; border: 1px solid var(--border-light);
  background-color: var(--bg-main); color: var(--text-main); font-size: 1rem;
}
.login-btn {
  width: 100%; padding: 12px; background-color: var(--color-button-buy); color: #fff;
  border: none; border-radius: 4px; font-size: 1rem; font-weight: bold; cursor: pointer; margin-top: 10px;
}

/* --- TABS --- */
.tabs { display: flex; gap: 10px; max-width: 95%; width: 95%; margin: 0 auto; overflow-x: auto; }
.tabs button {
  background: transparent; border: none; border-bottom: 3px solid transparent;
  color: var(--text-secondary); padding: 5px 5px; cursor: pointer; font-size: 1rem; font-weight: 600;
}
.tabs button:hover { color: var(--text-main); background-color: var(--bg-icon-hover-light); }
.tabs button.active { color: var(--color-accent); border-bottom-color: var(--color-accent); }

.admin-content {
  padding: 20px;
  flex: 1;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box; /* Essencial */
  overflow-x: hidden;
}

/* --- RESPONSIVIDADE (MOBILE) --- */
@media (max-width: 768px) {
  .header-content {
    justify-content: space-between; /* Espalha os itens: Esq - Meio - Dir */
    gap: 0;
  }
  
  /* Mobile: Reordena visualmente */
  .header-content h2 {
    order: 2; /* Título no meio */
    font-size: 1.4rem;
    margin: 0; /* Remove margin-right auto */
  }
  
  .refresh-btn {
    order: 1; /* Esquerda */
    margin: 0;
    border: none; background: transparent; padding: 5px;
  }
  
  .logout-btn {
    order: 3; /* Direita */
    margin: 0;
    border: none; padding: 5px;
    /* Mantém a cor vermelha mas remove a borda */
  }
  
  /* Esconde o texto dos botões no mobile */
  .btn-text {
    display: none;
  }
  
  .material-icons-outlined.icon {
    font-size: 28px; /* Ícone um pouco maior */
  }
  
  .tabs { justify-content: flex-start; }
}
</style>